import {
    Component,
    ViewEncapsulation,
    OnInit,
    inject,
    Input,
    Output,
    EventEmitter,
} from "@angular/core";
import { CerrixInputValidationRegistryService } from "@app/shared/directives/cerrix-input-fields/cerrix-input-validation-registry.service";
import { provideTranslocoScope, TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { CerrixInstructionsIconComponent } from "@app/shared/cerrix-instructions-icon/cerrix-instructions-icon.component";
import { AssetManagementModel } from "@models/assetmanagement/assetmanagementmodel";
import { CommonModule } from "@angular/common";
import { CerrixDialogService } from "@app/shared/organisms/cerrix-dialog/cerrix-dialog-service.component";
import { CerrixTreeItemPickerSingleComponent } from "@app/shared/organisms/cerrix-tree-item-picker-single/cerrix-tree-item-picker-single.component";
import { CerrixUserPickerComponent } from "@app/shared/organisms/cerrix-user-picker/cerrix-user-picker.component";
import { CerrixButtonComponent } from "@app/shared/atoms/cerrix-button/cerrix-button.component";
import { HttpClient } from "@angular/common/http";
import { finalize, firstValueFrom, Observable } from "rxjs";
import { Pages } from "@constants/pages/Pages";
import { TabService } from "@services/tabs/TabService";
import { ToastrService } from "ngx-toastr";
import {
    AssetManagementService,
    AssetProjection,
} from "@app/assetmanagement/services/assetmanagement-data.service";
import { CerrixConfirmDialogComponent } from "@app/shared/organisms/cerrix-confirm-dialog/cerrix-confirm-dialog.component";
import { IdGuidNameCombination } from "@models/generic/IdGuidNameCombination";
import { OwnershipInformationModel } from "@models/assetmanagement/ownershipinformationmodel";
import { CerrixTreeItem } from "@models/tree/CerrixTreeItem";
import { StandingdataDataService } from "@app/standingdata/shared/standingdata.service";
import { ModuleType } from "@enums/ModuleType";
import { CerrixInputValidationRequiredDirective } from "@app/shared/directives/cerrix-input-fields/cerrix-input-validation-required.directive";

@Component({
    selector: "app-edit-asset-ownership-information",
    templateUrl: "./edit-asset-ownership-information.component.html",
    styleUrls: ["./edit-asset-ownership-information.component.scss"],
    imports: [
        CommonModule,
        TranslocoPipe,
        CerrixInstructionsIconComponent,
        CerrixConfirmDialogComponent,
        CerrixButtonComponent,
        CerrixTreeItemPickerSingleComponent,
        CerrixUserPickerComponent,
        CerrixInputValidationRequiredDirective,
    ],
    providers: [CerrixInputValidationRegistryService, provideTranslocoScope("assetmanagement")],
    encapsulation: ViewEncapsulation.None,
})
export class EditAssetOwnershipInformationComponent implements OnInit {
    @Input() model: AssetManagementModel;

    @Output() modelChange: EventEmitter<AssetManagementModel> =
        new EventEmitter<AssetManagementModel>();
    @Output() closeEdit: EventEmitter<void> = new EventEmitter<void>();

    protected editModel: OwnershipInformationModel;
    protected organizationsList: CerrixTreeItem[] = [];
    protected userList: IdGuidNameCombination[] = [];

    protected editSaving = false;

    private _ds = inject(AssetManagementService);
    private _validationRegistry = inject(CerrixInputValidationRegistryService);
    private _promptService = inject(CerrixPromptService);
    private _transloco = inject(TranslocoService);
    private _tabService = inject(TabService);
    private _pages = inject(Pages);
    private _toastService = inject(ToastrService);
    private _dialogService = inject(CerrixDialogService);
    private _standingDataDS = inject(StandingdataDataService);
    private _http = inject(HttpClient);

    public confirmUnsavedChangesDialogName = "ownershipConfirmUnsavedChangesDialog";

    async ngOnInit() {
        // Ensure the scope JSON is loaded before the synchronous translate() calls below,
        // otherwise they resolve to the raw key while the lazy scope is still loading.
        await firstValueFrom(
            this._transloco.load(`assetmanagement/${this._transloco.getActiveLang()}`),
        );

        this.revertChanges();

        this.organizationsList = await firstValueFrom(
            this._standingDataDS.getOrganizationsIncludingSelected(
                ModuleType.AssetManagement,
                this.model.ownershipInformation.owningOrganizationId,
                true,
            ),
        );

        this.userList = await this.getUsers();
    }

    private async getUsers(): Promise<IdGuidNameCombination[]> {
        let params = "";

        if (this.model.ownershipInformation.ownerId) {
            params = `?selected=${this.model.ownershipInformation.ownerId}`;
        }

        return await firstValueFrom(
            this._http.get<IdGuidNameCombination[]>(`/api/users/list` + params),
        );
    }

    protected openUnsavedChangesConfirmDialog() {
        if (this.hasUnsavedChanges()) {
            this._dialogService.openDialog(this.confirmUnsavedChangesDialogName);
        } else {
            this.editCancel();
        }
    }

    protected closeUnsavedChangesConfirmDialog() {
        this._dialogService.closeDialog(this.confirmUnsavedChangesDialogName);
    }

    protected editCancel() {
        this.closeUnsavedChangesConfirmDialog();
        this.revertChanges();
        this.closeEdit.emit();
    }

    protected async editSave() {
        this.closeUnsavedChangesConfirmDialog();

        if (!this.validateFields()) {
            return;
        }

        this.editSaving = true;
        const savingPrompt = this._promptService.loader(
            this._transloco.translate("assetmanagement.detail.savingChanges"),
        );

        const storedAsset = await firstValueFrom(
            this.editSaveSection().pipe(
                finalize(() => {
                    savingPrompt.close();
                    this.editSaving = false;
                    this._tabService.refresh(this._pages.AssetManagementOverview);
                }),
            ),
        );

        if (storedAsset?.guid) {
            this.model = Object.assign(new AssetManagementModel(), storedAsset, {
                ownershipInformation: {
                    ownerId: storedAsset.ownershipInformation?.ownerId,
                    owningOrganizationId: storedAsset.ownershipInformation?.owningOrganizationId,
                },
            });
            this.modelChange.emit(this.model);
            this.revertChanges();

            this._toastService.success(
                this._transloco.translate("assetmanagement.detail.updateCompleted"),
            );
        }
    }

    private validateFields(): boolean {
        // A field the user never touched has never validated itself, so without this a missing
        // required value would pass unnoticed.
        this._validationRegistry.runValidators();

        if (this._validationRegistry.hasAnyErrors()) {
            this._toastService.warning(
                this._validationRegistry
                    .allMessages()
                    .map(
                        (e) =>
                            this._transloco.translate(
                                "assetmanagement.detail.validationMessagePrefix",
                            ) + e,
                    )
                    .join("\n"),
                this._transloco.translate("assetmanagement.detail.saveFailed"),
            );
            return false;
        }
        return true;
    }

    private hasUnsavedChanges(): boolean {
        return (
            this.model.ownershipInformation?.owningOrganizationId !==
                this.editModel.owningOrganizationId ||
            (this.model.ownershipInformation?.ownerId ?? null) !== (this.editModel.ownerId ?? null)
        );
    }

    private editSaveSection(): Observable<AssetProjection> {
        return this._ds.vbetaRegisterOwnership(this.model.guid, {
            owningOrganizationId: this.editModel.owningOrganizationId,
            ownerId: this.editModel.ownerId ?? null,
            version: this.model.version,
        });
    }

    private revertChanges(): void {
        this.editModel = {
            ownerId: this.model.ownershipInformation?.ownerId,
            owningOrganizationId: this.model.ownershipInformation?.owningOrganizationId,
        };
    }
}
